import { supabase } from './supabase';

type PaymentMethod = 'card' | 'upi';

interface PaymentDetails {
  orderId: string;
  amount: number;
  currency: string;
  method: PaymentMethod;
  upiId?: string;
}

export async function processPayment(details: PaymentDetails) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Update order with payment method
    const { error: orderError } = await supabase
      .from('orders')
      .update({
        payment_method: details.method,
        payment_status: 'processing',
        payment_gateway: details.method === 'upi' ? 'razorpay' : 'stripe'
      })
      .eq('id', details.orderId);

    if (orderError) throw orderError;

    // In production, integrate with actual payment gateways
    // For now, simulate payment processing
    const success = Math.random() > 0.1; // 90% success rate

    if (!success) {
      throw new Error('Payment processing failed');
    }

    // Update order status on success
    const { error: updateError } = await supabase
      .from('orders')
      .update({
        payment_status: 'completed',
        status: 'processing',
        payment_timestamp: new Date().toISOString()
      })
      .eq('id', details.orderId);

    if (updateError) throw updateError;

    return { success: true };
  } catch (error) {
    // Update order with error information
    await supabase
      .from('orders')
      .update({
        payment_status: 'failed',
        payment_error: error instanceof Error ? error.message : 'Payment failed',
        status: 'failed'
      })
      .eq('id', details.orderId);

    throw error;
  }
}

export async function validateUpiId(upiId: string): Promise<boolean> {
  // Basic UPI ID validation
  const upiRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/;
  return upiRegex.test(upiId);
}